import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
	Tabs,
	Tab,
	Tooltip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import { Condition, OnoffType, RegularLoopType } from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import MeditationListSummaryFilterSection from './components/RegularWeekSummaryFilterSection';
import SetMeditationOrderDialog from './components/SetRegularWeekOrderDialog';
import { useGetRegularListQuery } from '@/api/task/regularApiSlice';
import {
	RegularFilter,
	TaskScheduledDTO,
} from '@/api/DTO/task/regular.interface';
import PublishedChip from '@/common/components/PublishedChip';
import { PUBLISHED_LISTB } from '@/common/constants/common';
import WarningIcon from '@mui/icons-material/Warning';

export interface DataGrid {
	updatedAt?: string;
	publishedStartAt?: string;
	publishedEndAt?: string;
	createdAt?: string;
	id?: string;
}

export default function RegularWeekSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('regularWeek');

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
		if (newValue == 'regularDay') navigate(`/task-manage/regular-day-list`);
		if (newValue == 'regularMonth')
			navigate(`/task-manage/regular-day-list/month`);
	};

	const defaultPropertyFilter: RegularFilter = {
		pagination: initialPagination,
		searchString: '',
		type: RegularLoopType.Week,
		sortings: [],
		publishedList: [],
	};
	const defaultValues: DefaultValues<RegularFilter> = defaultPropertyFilter;
	const methods = useForm<RegularFilter>({
		defaultValues,
	});
	const transformedSearchParams: RegularFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'publishedList',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetRegularListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: TaskScheduledDTO[]) => {
		if (response == null) return;
		const newResponse: DataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				publishedStartAt: !item.publishedStartAt
					? '-'
					: dayjs(item.publishedStartAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				publishedEndAt: !item.publishedEndAt
					? '-'
					: dayjs(item.publishedEndAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as RegularFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('regularTask.taskName'),
			flex: 15,
		},
		{
			field: 'finishedConditionName',
			headerName: t('regularTask.completionConditions'),
			flex: 15,
		},
		{
			field: 'rewardPoints',
			headerName: t('regularTask.rewardPoints'),
			flex: 15,
		},
		{
			field: 'rewardDesc',
			headerName: t('regularTask.rewardItem'),
			flex: 15,
			renderCell: (params: any) => params.row.rewardDesc || '-',
		},
		{
			field: 'published',
			headerName: t('regularTask.taskStatus'),
			flex: 15,
			renderCell: (params: any) => (
				<>
					<PublishedChip
						published={params.row.published}
						publishList={PUBLISHED_LISTB}
					/>
					{params.row.published == OnoffType.on &&
						params.row.allRewardUnpublished && (
							<Tooltip
								title={`${t('regularTask.statusTips')}`}
								sx={{ color: '#FA9E26' }}
							>
								<WarningIcon />
							</Tooltip>
						)}
				</>
			),
		},
		{
			field: 'publishedStartAt',
			headerName: t('achievementList.pushedTime'),
			flex: 10,
		},

		{
			field: 'publishedEndAt',
			headerName: t('achievementList.pulledTime'),
			flex: 10,
		},
		{
			field: 'updatedAt',
			headerName: t('achievementList.updateTime'),
			flex: 10,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/task-manage/regular-day-list/week/info/${params.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.regularTask')}</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetMeditationOrderDialog />
					<Button
						variant='contained'
						style={{ alignItems: 'center' }}
						endIcon={<NoteAltOutlinedIcon />}
						onClick={() => navigate(`/task-manage/regular-day-list/week/add`)}
					>
						{t('regularTask.createNewTask')}
					</Button>
				</Stack>
			</Box>

			<Tabs value={currentTab} onChange={handleTabChange} sx={{ marginTop: 3 }}>
				<Tab
					label={
						<Typography variant='body1'>
							{t('drawerListItems.dayTask')}
						</Typography>
					}
					value='regularDay'
				/>
				<Tab
					label={
						<Typography variant='body1'>
							{t('drawerListItems.weekTask')}
						</Typography>
					}
					value='regularWeek'
				/>
				<Tab
					label={
						<Typography variant='body1'>
							{t('drawerListItems.monthTask')}
						</Typography>
					}
					value='regularMonth'
				/>
			</Tabs>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<MeditationListSummaryFilterSection></MeditationListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
